import React, { useMemo } from 'react';
import {
    ROLE_ADMIN,
    ROLE_VACATION_DP,
    useAuth,
} from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import {
    useMyVacationRequestsQuery,
    useVacationReceiptMutations,
    useVacationReceiptsQuery,
} from '../../hooks/data';
import { buildReceiptRequestOptions } from '../../utils/vacationReceiptLinks';
import type { VacationReceipt, VacationReceiptUpload } from '../../models/vacationReceipt.types';
import { NoCollaboratorNotice } from './components';
import {
    MISSING_COLLABORATOR_EMAIL_DETAIL,
    MISSING_COLLABORATOR_EMAIL_TITLE,
    MISSING_SESSION_EMAIL_DETAIL,
    MISSING_SESSION_EMAIL_TITLE,
} from './vacationReceipts.definitions';
import { VacationReceipts } from './VacationReceipts';
import type { VacationReceiptsPanelProps } from './vacationReceipts.types';

export const VacationReceiptsPanel: React.FC<VacationReceiptsPanelProps> = ({
    collaboratorId,
    collaboratorEmail,
    canEdit = true,
    title,
    description,
}) => {
    const { user, userHasRole } = useAuth();
    const toast = useToast();

    const hasManagementRole =
        userHasRole(ROLE_ADMIN) ||
        userHasRole(ROLE_VACATION_DP);

    const { receipts, isLoading, error } = useVacationReceiptsQuery(collaboratorId);

    const canFetchRequests = canEdit && hasManagementRole;
    const { requests } = useMyVacationRequestsQuery(
        canFetchRequests ? collaboratorEmail : undefined
    );
    const requestOptions = useMemo(
        () => buildReceiptRequestOptions(requests, receipts),
        [requests, receipts]
    );
    const { uploadReceipt, deleteReceipt, uploadProgress, deletingId } =
        useVacationReceiptMutations(collaboratorId);

    if (!collaboratorId) return <NoCollaboratorNotice />;

    const canManage = canEdit && hasManagementRole && Boolean(user?.email);

    const handleUpload = (upload: VacationReceiptUpload) => {
        if (!user?.email) {
            toast.error(MISSING_SESSION_EMAIL_TITLE, MISSING_SESSION_EMAIL_DETAIL);
            return;
        }

        const ownerEmail = collaboratorEmail?.trim();
        if (!ownerEmail) {
            toast.error(MISSING_COLLABORATOR_EMAIL_TITLE, MISSING_COLLABORATOR_EMAIL_DETAIL);
            return;
        }

        uploadReceipt.mutate({
            ...upload,
            collaboratorId,
            collaboratorEmail: ownerEmail,
            uploadedBy: user.email,
        });
    };

    const handleDelete = (receipt: VacationReceipt) => deleteReceipt.mutate(receipt);

    return (
        <VacationReceipts
            receipts={receipts}
            isLoading={isLoading}
            error={error}
            canEdit={canManage}
            title={title}
            description={description}
            isUploading={uploadReceipt.isPending}
            uploadFailed={uploadReceipt.isError}
            uploadProgress={uploadProgress}
            deletingId={deletingId}
            requestOptions={requestOptions}
            onUpload={handleUpload}
            onDelete={handleDelete}
        />
    );
};

export default VacationReceiptsPanel;
